import { LexoRank } from 'lexorank';
import { Rank } from '../dto/common.dto';
import { deepCopy } from './object.utils';

export function getHighestRankFrom(items: { rank: Rank }[]): LexoRank {
  return items
    .map(_ => LexoRank.parse(_.rank ?? LexoRank.min().toString()))
    .reduce((res, item) => {
      if (item.compareTo(res) > 0) {
        return item;
      }

      return res;
    }, LexoRank.min());
}

export function getMinimumRank(): Rank {
  return <Rank>LexoRank.min().toString();
}

export function getMediumRank(): Rank {
  return <Rank>LexoRank.middle().toString();
}

export function getHigherRankThan(rank: Rank): Rank {
  return <Rank>LexoRank.parse(rank).genNext().toString();
}

export function getLowerRankThan(rank: Rank): Rank {
  return <Rank>LexoRank.parse(rank).genPrev().toString();
}

export function getRankBetween(rankA: Rank | undefined, rankB: Rank | undefined): Rank {
  if (rankA && rankB) {
    if (rankA === rankB) {
      return getHigherRankThan(rankB);
    } else {
      return <Rank>LexoRank.parse(rankA).between(LexoRank.parse(rankB)).toString();
    }
  }

  if (rankA) {
    return getHigherRankThan(rankA);
  }

  if (rankB) {
    return getLowerRankThan(rankB);
  }

  return getMediumRank();
}

export function compareRanksFrom(a: { rank?: Rank }, b: { rank?: Rank }): number {
  return LexoRank.parse(a.rank ?? '').compareTo(LexoRank.parse(b.rank ?? ''));
}

export function convertToRank(lexorank: LexoRank): Rank {
  return <Rank>lexorank.toString();
}

export function moveItemRankInList(itemList: { rank: Rank }[], item: { rank: Rank }, direction: 'up' | 'down'): Rank {
  if (itemList.length <= 1) {
    return item.rank;
  }

  const sortedItemList = deepCopy(itemList).sort(compareRanksFrom);

  const itemIndex = sortedItemList.findIndex(_ => _.rank === item.rank);

  if (direction === 'up') {
    if (itemIndex <= 0) {
      return item.rank;
    }

    return getRankBetween(sortedItemList[itemIndex - 2]?.rank, sortedItemList[itemIndex - 1]?.rank);
  }

  if (itemIndex >= (itemList.length - 1)) {
    return item.rank;
  }

  return getRankBetween(sortedItemList[itemIndex + 1]?.rank, sortedItemList[itemIndex + 2]?.rank);
}
